import React, { useState, useEffect } from 'react';
import {
  Row, Col, Spin, Alert, Table, Tag, Space, Card,
  Button, Select, Switch, Empty, message, Tooltip, Badge
} from 'antd';
import {
  BellOutlined, CheckOutlined, ExclamationCircleOutlined,
  ReloadOutlined, FireOutlined, CloseOutlined,
  EyeOutlined, ThunderboltOutlined, DeleteOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { notificationService } from '../services/api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import '../styles/Phase5.css';

dayjs.extend(relativeTime);

const { Option } = Select;

const Notifications = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    alert_type: null,
    priority: null,
    unread_only: false,
  });

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  useEffect(() => {
    fetchData();
  }, [filters, pagination.current, pagination.pageSize]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const skip = (pagination.current - 1) * pagination.pageSize;

      const params = {
        limit: pagination.pageSize,
        skip,
        ...(filters.unread_only && { unread_only: true }),
        ...(filters.alert_type && { alert_type: filters.alert_type }),
        ...(filters.priority && { priority: filters.priority }),
      };

      const [statsData, notifData] = await Promise.all([
        notificationService.getStats().catch(() => null),
        notificationService.getAll(params).catch(() => []),
      ]);

      setStats(statsData);
      setNotifications(notifData || []);

      if (statsData) {
        setPagination(prev => ({ ...prev, total: statsData.total }));
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Failed to load notifications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id, e) => {
    if (e) e.stopPropagation();
    try {
      await notificationService.markAsRead(id);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n)
      );
      setStats(prev => prev ? { ...prev, unread: Math.max(0, prev.unread - 1) } : prev);
      message.success('Marked as read');
    } catch (err) {
      message.error('Failed to mark as read');
    }
  };

  const handleMarkAllRead = async () => {
    setActionLoading(true);
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() })));
      setStats(prev => prev ? { ...prev, unread: 0 } : prev);
      message.success('All notifications marked as read');
    } catch (err) {
      message.error('Failed to mark all as read');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDismiss = async (id, e) => {
    if (e) e.stopPropagation();
    try {
      await notificationService.dismiss(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      setStats(prev => prev ? { ...prev, total: prev.total - 1 } : prev);
      message.success('Notification dismissed');
    } catch (err) {
      message.error('Failed to dismiss notification');
    }
  };

  const handleGenerateAlerts = async () => {
    setActionLoading(true);
    try {
      const result = await notificationService.generateAlerts();
      message.success(result.message || 'Alert check completed');
      fetchData();
    } catch (err) {
      message.error('Failed to generate alerts');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRowClick = async (record) => {
    if (!record.is_read) {
      await notificationService.markAsRead(record.id).catch(() => {});
      setNotifications(prev =>
        prev.map(n => n.id === record.id ? { ...n, is_read: true } : n)
      );
      setStats(prev => prev ? { ...prev, unread: Math.max(0, prev.unread - 1) } : prev);
    }
    if (record.action_url) {
      navigate(record.action_url);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return '#ff4d4f';
      case 'high': return '#fa8c16';
      case 'medium': return '#1890ff';
      case 'normal': return '#1890ff';
      default: return '#8c8c8c';
    }
  };

  const getPriorityTag = (priority) => {
    const colors = {
      critical: 'red',
      high: 'orange',
      medium: 'blue',
      normal: 'blue',
      low: 'default',
    };
    return <Tag color={colors[priority] || 'default'}>{priority?.toUpperCase()}</Tag>;
  };

  const getPriorityIcon = (priority) => {
    if (priority === 'critical') return <FireOutlined style={{ color: '#ef4444' }} />;
    if (priority === 'high') return <ExclamationCircleOutlined style={{ color: '#f97316' }} />;
    return null;
  };

  const getTypeTag = (type) => {
    const colors = {
      contract_expiring: 'volcano',
      auto_renewal: 'orange',
      budget_threshold: 'gold',
      spend_anomaly: 'magenta',
    };
    const labels = {
      contract_expiring: 'Contract',
      auto_renewal: 'Renewal',
      budget_threshold: 'Budget',
      spend_anomaly: 'Anomaly',
    };
    return <Tag color={colors[type] || 'default'}>{labels[type] || type}</Tag>;
  };

  const columns = [
    {
      title: '',
      key: 'priority_icon',
      width: 40,
      render: (_, record) => getPriorityIcon(record.priority),
    },
    {
      title: 'Title',
      key: 'title',
      render: (_, record) => (
        <div>
          <span style={{ fontWeight: record.is_read ? 400 : 600 }}>{record.title}</span>
          {record.message && (
            <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 2 }}>
              {record.message.length > 100 ? record.message.substring(0, 100) + '...' : record.message}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'alert_type',
      key: 'alert_type',
      width: 120,
      render: (type) => getTypeTag(type),
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (priority) => getPriorityTag(priority),
    },
    {
      title: 'Time',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      render: (date) => (
        <Tooltip title={dayjs(date).format('MMM D, YYYY h:mm A')}>
          <span style={{ color: '#64748b', fontSize: '0.85rem' }}>
            {dayjs(date).fromNow()}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          {!record.is_read && (
            <Tooltip title="Mark as read">
              <Button
                type="text"
                size="small"
                icon={<EyeOutlined />}
                onClick={(e) => handleMarkAsRead(record.id, e)}
              />
            </Tooltip>
          )}
          <Tooltip title="Dismiss">
            <Button
              type="text"
              size="small"
              danger
              icon={<CloseOutlined />}
              onClick={(e) => handleDismiss(record.id, e)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const criticalHighCount = stats
    ? (stats.by_priority?.critical || 0) + (stats.by_priority?.high || 0)
    : 0;

  const typeBreakdown = stats?.by_type
    ? Object.entries(stats.by_type).map(([type, count]) => `${type}: ${count}`).join(', ')
    : 'None';

  if (error) {
    return (
      <div className="p5-page">
        <Alert message="Error" description={error} type="error" showIcon />
      </div>
    );
  }

  return (
    <div className="p5-page">
      {/* Page Header */}
      <div className="p5-page-header p5-flex-between" style={{ alignItems: 'flex-start' }}>
        <div>
          <h1 className="p5-page-title">Notification Center</h1>
          <p className="p5-page-subtitle">
            View alerts, contract reminders, budget warnings, and spend anomalies
          </p>
        </div>
        <Space>
          <Select
            placeholder="Type"
            style={{ width: 150 }}
            allowClear
            value={filters.alert_type}
            onChange={(val) => {
              setFilters(prev => ({ ...prev, alert_type: val }));
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
          >
            <Option value="contract_expiring">Contract Expiring</Option>
            <Option value="auto_renewal">Auto Renewal</Option>
            <Option value="budget_threshold">Budget Threshold</Option>
            <Option value="spend_anomaly">Spend Anomaly</Option>
          </Select>
          <Select
            placeholder="Priority"
            style={{ width: 120 }}
            allowClear
            value={filters.priority}
            onChange={(val) => {
              setFilters(prev => ({ ...prev, priority: val }));
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
          >
            <Option value="critical">Critical</Option>
            <Option value="high">High</Option>
            <Option value="normal">Normal</Option>
            <Option value="low">Low</Option>
          </Select>
          <Space>
            <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Unread only</span>
            <Switch
              checked={filters.unread_only}
              onChange={(checked) => {
                setFilters(prev => ({ ...prev, unread_only: checked }));
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              size="small"
            />
          </Space>
          <Button
            className="p5-btn p5-btn--ghost"
            icon={<CheckOutlined />}
            onClick={handleMarkAllRead}
            loading={actionLoading}
            disabled={!stats?.unread}
          >
            Mark All Read
          </Button>
          <Button
            className="p5-btn p5-btn--ghost"
            icon={<ThunderboltOutlined />}
            onClick={handleGenerateAlerts}
            loading={actionLoading}
          >
            Generate Alerts
          </Button>
          <Button
            className="p5-btn p5-btn--ghost"
            icon={<ReloadOutlined />}
            onClick={fetchData}
          >
            Refresh
          </Button>
        </Space>
      </div>

      <Spin spinning={loading}>
        {/* Stats Grid */}
        <div className="p5-stats-grid p5-stagger">
          <div className="p5-stat-card p5-animate-fadeUp">
            <div className="p5-stat-icon p5-stat-icon--amber">
              <BellOutlined />
            </div>
            <div className="p5-stat-label">Total</div>
            <div className="p5-stat-value">{stats?.total || 0}</div>
          </div>

          <div className="p5-stat-card p5-animate-fadeUp">
            <div className="p5-stat-icon p5-stat-icon--teal">
              <Badge dot={!!stats?.unread}>
                <BellOutlined style={{ color: 'inherit' }} />
              </Badge>
            </div>
            <div className="p5-stat-label">Unread</div>
            <div className="p5-stat-value" style={{ color: (stats?.unread || 0) > 0 ? 'var(--p5-amber-500)' : undefined }}>
              {stats?.unread || 0}
            </div>
          </div>

          <div className="p5-stat-card p5-animate-fadeUp">
            <div className="p5-stat-icon p5-stat-icon--rose">
              <ExclamationCircleOutlined />
            </div>
            <div className="p5-stat-label">Critical / High</div>
            <div className="p5-stat-value" style={{ color: criticalHighCount > 0 ? 'var(--p5-rose-500)' : undefined }}>
              {criticalHighCount}
            </div>
          </div>

          <div className="p5-stat-card p5-animate-fadeUp">
            <div className="p5-stat-icon p5-stat-icon--emerald">
              <FireOutlined />
            </div>
            <div className="p5-stat-label">By Type</div>
            <div className="p5-stat-value" style={{ fontSize: '0.85rem', fontWeight: 500 }}>
              {stats?.by_type && Object.keys(stats.by_type).length > 0
                ? Object.keys(stats.by_type).length + ' types'
                : 'None'}
            </div>
          </div>
        </div>

        {/* Notification Table */}
        <div className="p5-table-wrapper p5-animate-fadeUp">
          {notifications.length === 0 && !loading ? (
            <Empty
              description="No notifications"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ padding: 64 }}
            />
          ) : (
            <Table
              dataSource={notifications}
              columns={columns}
              rowKey="id"
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50'],
                onChange: (page, pageSize) => {
                  setPagination(prev => ({ ...prev, current: page, pageSize }));
                },
              }}
              onRow={(record) => ({
                onClick: () => handleRowClick(record),
                style: {
                  cursor: record.action_url ? 'pointer' : 'default',
                  background: record.is_read ? '#fff' : '#f6ffed',
                  borderLeft: `4px solid ${getPriorityColor(record.priority)}`,
                },
              })}
            />
          )}
        </div>
      </Spin>
    </div>
  );
};

export default Notifications;
